import React, { useState, useEffect } from "react";
import ModalModern from "../ModalModern";
import ModernPopupHeader from "../common/ModernPopupHeader";
import ModernPopupBody from "../common/ModernPopupBody";
import LoadingPlaceholder from "../../loading/LoadingPlaceholder";
import {
  fetchReportDetail,
  downloadReportPdf,
} from "../../../../api/finances/reportAPI";

function MonthlyReportModal({
  snapshotUuid,
  content,
  onClose,
  isPaidPlan,
  onUpgradeClick,
}) {
  const [reportDetail, setReportDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [apiError, setApiError] = useState("");

  const reports_language = content.subscriber_view.reports;
  const monthly_reports_details_language =
    reports_language.monthly_reports.snapshot_detail_modal;

  useEffect(() => {
    const loadSnapshotDetail = async () => {
      setIsLoading(true);
      setApiError("");

      const result = await fetchReportDetail(snapshotUuid);

      if (result.success) {
        setReportDetail(result.data);
      } else {
        setApiError(result.error || "Failed to load report details.");
      }

      setIsLoading(false);
    };

    if (snapshotUuid) {
      loadSnapshotDetail();
    }
  }, [snapshotUuid]);

  const handleDownloadPdf = async () => {
    if (!snapshotUuid || isDownloading) return;

    setIsDownloading(true);
    setApiError("");

    const result = await downloadReportPdf(snapshotUuid, "pdf");

    if (result.success) {
      // Trigger native browser download
      const url = window.URL.createObjectURL(result.blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        result.filename || `MFS_${reportDetail?.period_key || "Report"}.pdf`,
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } else {
      setApiError(result.error || "Failed to download PDF report.");
    }

    setIsDownloading(false);
  };

  const formatPeriod = (periodKey) => {
    if (!periodKey) return "";
    const [year, month] = periodKey.split("-");
    const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
    return date.toLocaleString("default", { month: "long", year: "numeric" });
  };

  const snapshotData = reportDetail?.snapshot || {};
  const summary = snapshotData.summary || {};
  const spending = snapshotData.spending || {};
  const periodKey = reportDetail?.period_key || snapshotData.period?.period_key;

  return (
    <ModalModern onClose={onClose}>
      <ModernPopupHeader onClose={onClose}>
        <div className="header-content-modern-row">
          <h3 className="transaction-popup-title">
            {periodKey
              ? `Monthly Summary — ${formatPeriod(periodKey)}`
              : "Monthly Report"}
          </h3>
        </div>
      </ModernPopupHeader>

      <ModernPopupBody>
        {isLoading ? (
          <div className="report-modal-loading">
            <LoadingPlaceholder rows={4} type="card-small" />
          </div>
        ) : apiError ? (
          <div
            style={{
              padding: "24px",
              textAlign: "center",
              color: "#EF4444",
              fontSize: "14px",
            }}
          >
            {apiError}
          </div>
        ) : (
          <div className="report-modal-content">
            {/* 1. Executive Key Financial Cards */}
            <div className="report-metrics-grid">
              <div className="report-metric-card">
                <span className="report-metric-label">
                  {monthly_reports_details_language.income}
                </span>
                <strong className="report-metric-value positive">
                  $
                  {summary.total_income?.amount?.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }) ?? "0.00"}
                </strong>
                <small className="report-metric-subtext">
                  {summary.total_income?.label}
                </small>
              </div>

              <div className="report-metric-card">
                <span className="report-metric-label">
                  {monthly_reports_details_language.spending}
                </span>
                <strong className="report-metric-value negative">
                  $
                  {summary.total_spending?.amount?.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }) ?? "0.00"}
                </strong>
                <small className="report-metric-subtext">
                  {summary.total_spending?.label}
                </small>
              </div>

              <div className="report-metric-card">
                <span className="report-metric-label">
                  {monthly_reports_details_language.cashflow}
                </span>
                <strong
                  className={`report-metric-value ${
                    (summary.net_cashflow ?? 0) >= 0 ? "positive" : "negative"
                  }`}
                >
                  {(summary.net_cashflow ?? 0) >= 0 ? "+" : ""}$
                  {summary.net_cashflow?.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }) ?? "0.00"}
                </strong>
              </div>
            </div>

            {/* 2. Breakdown Section: Top Categories & Top Merchants */}
            <div className="report-breakdown-grid">
              {/* Top Categories */}
              <div className="report-section-column">
                <h4 className="report-section-title">
                  {monthly_reports_details_language.categories}
                </h4>
                <div className="report-list-group">
                  {spending.top_categories?.length > 0 ? (
                    spending.top_categories.map((cat, idx) => (
                      <div key={idx} className="report-list-row">
                        <span>{cat.label}</span>
                        <strong>
                          $
                          {cat.total_amount?.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </strong>
                      </div>
                    ))
                  ) : (
                    <span style={{ fontSize: "13px", color: "#6B7280" }}>
                      {monthly_reports_details_language.non_categories}
                    </span>
                  )}
                </div>
              </div>

              {/* Top Merchants */}
              <div className="report-section-column">
                <h4 className="report-section-title">
                  {monthly_reports_details_language.merchants}
                </h4>
                <div className="report-list-group">
                  {spending.top_merchants?.length > 0 ? (
                    spending.top_merchants.map((mer, idx) => (
                      <div key={idx} className="report-list-row">
                        <span className="report-merchant-name">
                          {mer.merchant}
                        </span>
                        <strong>
                          $
                          {mer.total_amount?.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </strong>
                      </div>
                    ))
                  ) : (
                    <span style={{ fontSize: "13px", color: "#6B7280" }}>
                      {monthly_reports_details_language.non_merchants}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* 3. Pro Feature Callout / PDF Download Footer */}
            <div className="report-pdf-callout">
              <div>
                <strong className="report-pdf-title">
                  {monthly_reports_details_language.pdf_card.title}
                </strong>
                <span className="report-pdf-subtitle">
                  {isPaidPlan
                    ? "Includes full transactional appendices and category trends."
                    : "Upgrade to Pro to download formal PDF financial statements."}
                </span>
              </div>

              {isPaidPlan ? (
                <button
                  className="cta-button secondary"
                  onClick={handleDownloadPdf}
                  disabled={isDownloading}
                >
                  {isDownloading ? "Downloading..." : "Download"}
                </button>
              ) : (
                <button
                  className="cta-button primary btn-nowrap"
                  onClick={onUpgradeClick}
                >
                  {monthly_reports_details_language.pdf_card.upgrade_btn}
                </button>
              )}
            </div>
          </div>
        )}
      </ModernPopupBody>
    </ModalModern>
  );
}

export default MonthlyReportModal;
